import { configureStore } from '@reduxjs/toolkit';
import { type Persistor, persistReducer, persistStore } from 'redux-persist';
// Import the ESM build (redux-persist/es/storage), not the CJS one
// (redux-persist/lib/storage). When Vite production-bundles the CJS
// build it wraps it with esbuild's __toESM(..., 1) helper, which sets
// `default = <module namespace>` instead of `default = mod.default`.
// At runtime that turns `storage` into `{ __esModule: true, default: realWrapper }`
// — an object with no .getItem — and persistStore() throws
// `r.getItem is not a function` on hydration, blanking the page.
// The es/ subpath is real ESM so the interop is straightforward.
import storage from 'redux-persist/es/storage';
import reducers from './reducers';

// redux-persist's `storage` wrapper reads `window.localStorage` at
// module load (via getStorage). On the server there is no `self`, so
// it returns a noop. We further substitute our own noop here so the
// import shape stays predictable; the persistor itself is also gated
// below so PersistGate is never instantiated server-side.
const isBrowser = typeof window !== 'undefined';
const noopStorage = {
   getItem: (_key: string) => Promise.resolve(null),
   setItem: (_key: string, value: any) => Promise.resolve(value),
   removeItem: (_key: string) => Promise.resolve(),
};
const persistStorage = isBrowser ? storage : noopStorage;

const persistConfig = {
  key: 'root',
  storage: persistStorage,
};

const persistedReducer = persistReducer(persistConfig, reducers);

const configureAppStore = (preloadedState: any = {}) => {
  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),
  });

  return store;
}

export type AppStore = ReturnType<typeof configureAppStore>;

// Factories instead of module-level singletons. With ssr: false the
// store wouldn't actually leak today (we never dispatch into it
// server-side), but a factory shape makes it impossible to *start*
// leaking when any route flips to body SSR. Root.tsx creates one
// instance per mount via useState's lazy initializer.
export function createStore(preloadedState?: any): AppStore {
   return configureAppStore(preloadedState);
}

export function createPersistor(store: AppStore): Persistor | null {
   return isBrowser ? persistStore(store) : null;
}
